import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Crosshair, Target, History, TrendingUp, Settings, Menu, LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NAV_ITEMS: { to: string; icon: LucideIcon; label: string }[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard'   },
  { to: '/session',   icon: Crosshair,       label: 'Log Session' },
  { to: '/drills',    icon: Target,          label: 'Drills'      },
  { to: '/history',   icon: History,         label: 'History'     },
  { to: '/progress',  icon: TrendingUp,      label: 'Progress'    },
];

const ADMIN_ITEMS: { to: string; icon: LucideIcon; label: string }[] = [
  { to: '/admin', icon: Settings, label: 'Admin' },
];

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive     = profile?.subscriptionStatus === 'active';
  const trialEndsAt  = profile?.trialEndsAt;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil(
        ((trialEndsAt as any).toDate
          ? (trialEndsAt as any).toDate().getTime()
          : new Date(trialEndsAt as any).getTime()
        - Date.now()) / 86_400_000))
    : null;
  const trialExpired = trialDaysLeft === 0;
  const trialUrgent  = trialDaysLeft !== null && trialDaysLeft <= 3;

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="sidebar__logo">
        <div className="sidebar__logo-text">147<span>coach</span></div>
        <div className="sidebar__logo-sub">Performance Analytics</div>
      </div>

      {/* Nav */}
      <nav className="sidebar__nav">
        {NAV_ITEMS.map(item => (
          <SidebarLink key={item.to} {...item} onNavigate={() => setMobileMenuOpen(false)} />
        ))}

        {profile?.isAdmin && (
          <>
            <div className="sidebar__divider" />
            {ADMIN_ITEMS.map(item => (
              <SidebarLink key={item.to} {...item} onNavigate={() => setMobileMenuOpen(false)} />
            ))}
          </>
        )}
      </nav>

      {/* Upgrade CTA */}
      {!isActive && (
        <div style={{ padding: '0 16px 16px' }}>
          <button
            onClick={() => { navigate('/subscribe'); setMobileMenuOpen(false); }}
            style={{
              width: '100%',
              padding: '10px 14px',
              background: trialExpired ? 'rgba(220,38,38,0.15)' : 'rgba(201,168,76,0.1)',
              border: `1px solid ${trialExpired ? 'rgba(220,38,38,0.4)' : 'rgba(201,168,76,0.3)'}`,
              borderRadius: 8,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = trialExpired ? 'rgba(220,38,38,0.22)' : 'rgba(201,168,76,0.16)')}
            onMouseLeave={e => (e.currentTarget.style.background = trialExpired ? 'rgba(220,38,38,0.15)' : 'rgba(201,168,76,0.1)')}
          >
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.6rem', letterSpacing: '0.1em',
              textTransform: 'uppercase', color: trialExpired ? '#f87171' : 'var(--gold)', marginBottom: 3,
            }}>
              {trialExpired ? '⚠ Trial expired' : trialUrgent ? `⚡ ${trialDaysLeft}d left` : '◌ Free Trial'}
            </div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.58rem',
              color: trialExpired ? 'rgba(248,113,113,0.7)' : 'var(--chalk-faint)', letterSpacing: '0.05em',
            }}>
              {trialExpired ? 'Subscribe to continue →' : 'Upgrade to Pro →'}
            </div>
          </button>
        </div>
      )}

      {/* User footer */}
      <div className="sidebar__footer">
        <div className="sidebar__footer-name">{profile?.displayName ?? 'Player'}</div>
        <div className="sidebar__footer-plan">{isActive ? '✓ Pro' : '◌ Free Trial'}</div>
        <button className="sidebar__signout" onClick={handleSignOut}>
          Sign Out →
        </button>
      </div>
    </>
  );

  return (
    <div className="layout">

      {/* ─── Mobile top bar ─── */}
      <div className="mobile-bar">
        <span className="mobile-bar__logo">147<span>coach</span></span>
        <button className="mobile-bar__menu" onClick={() => setMobileMenuOpen(true)}>
          <Menu size={20} />
        </button>
      </div>

      {/* ─── Mobile nav drawer ─── */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav--open' : ''}`}>
        <div className="mobile-nav__overlay" onClick={() => setMobileMenuOpen(false)} />
        <div className="mobile-nav__drawer">
          {sidebarContent}
        </div>
      </div>

      {/* ─── Desktop sidebar ─── */}
      <aside className="sidebar">
        {sidebarContent}
      </aside>

      {/* ─── Main content ─── */}
      <main className="main-content">
        {children}
      </main>

    </div>
  );
}

function SidebarLink({
  to, icon: Icon, label, onNavigate,
}: {
  to: string; icon: LucideIcon; label: string; onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
      }
    >
      <Icon size={15} className="sidebar__icon" strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}